/**
 * script-prompter.js — a prompter strip: the script scrolls by itself, or
 * follows the reader's voice (VoiceFollow), in any document — the page or a
 * Document Picture-in-Picture window that stays over other apps.
 *
 * Born inside /webcam-recorder's blade (2026-09-04); moved here 26.10.2026
 * when /screen-recorder needed the same strip in its floating window (a
 * screen recording's prompter cannot live on the page — the person is on the
 * screen they record). One owner of «where the strip is»: the matcher stays
 * VoiceFollow's (voice-follow.js), the strip only turns a word index into a
 * scroll, and the timed scroll is the fallback — unsupported browser,
 * permission, a recogniser that stops delivering.
 *
 *   var p = ScriptPrompter.create({
 *       scrollEl, textEl,            // the strip and its text (any document)
 *       voiceHost,                   // element that gets `voiceClass` while the voice leads
 *       wordClass: 'wr-tp-w', curClass: 'is-cur', voiceClass: 'is-voice',
 *       speed: function () { return px per second },
 *       slug, lang,                  // for the recogniser (WebSpeechSession)
 *       onVoiceState: function (key|null) {},   // 'tp_voice_listening' | …_locked | …_searching | …_fallback
 *       onEvent: function (name, payload) {},   // telemetry: tp_voice_start, tp_voice_fatal
 *       onDebug: function (dbg) {},             // VoiceFollow's per-utterance internals (tests)
 *       readingLine: 0.38            // where the current word sits in the strip
 *   });
 *   p.setText(text); p.setVoice(bool);
 *   p.start() — from the top; p.pause(); p.unpause() — on from where it stands; p.stop();
 *   p.voiceActive()
 *
 * Scrolling is the strip's own scrollTop — never the page's (CLAUDE.md, no
 * auto-scroll). The animation frames come from the strip's window: a
 * floating window keeps animating while the page under it is hidden.
 *
 * Guard: tests/js/script-prompter.test.mjs.
 */
(function () {
    'use strict';
    if (window.ScriptPrompter) return;

    function create(o) {
        o = o || {};
        var scroll = o.scrollEl, text = o.textEl;
        var doc = text.ownerDocument, win = doc.defaultView || window;
        var wordClass = o.wordClass || 'tp-w', curClass = o.curClass || 'is-cur', voiceClass = o.voiceClass || 'is-voice';
        var line = typeof o.readingLine === 'number' ? o.readingLine : 0.38;
        var want = false, txt = '', words = [], cur = null, follower = null, voiceOn = false, scrolling = false, raf = 0;
        function vf() { return window.VoiceFollow && window.VoiceFollow.isSupported() ? window.VoiceFollow : null; }
        function state(k) { try { if (o.onVoiceState) o.onVoiceState(k); } catch (_) {} }
        function ev(n, p) { try { if (o.onEvent) o.onEvent(n, p || {}); } catch (_) {} }
        function host(on) { if (o.voiceHost) o.voiceHost.classList.toggle(voiceClass, !!on); }
        function render() {
            words = []; cur = null;
            if (!(want && vf())) { text.textContent = txt; return; }
            // one span per word: the strip scrolls to the word the voice is on
            text.textContent = '';
            txt.split(/(\s+)/).forEach(function (p) {
                if (!p) return;
                if (/^\s+$/.test(p)) { text.appendChild(doc.createTextNode(p)); return; }
                var s = doc.createElement('span'); s.className = wordClass; s.textContent = p; text.appendChild(s); words.push(s);
            });
        }
        function stopVoice() {
            voiceOn = false;
            if (follower) { try { follower.destroy(); } catch (_) {} follower = null; }
            host(false); scroll.style.scrollBehavior = '';
            state(null);
        }
        function startVoice() {
            var V = vf();
            if (!want || !V || !words.length) return false;
            var tokens = words.map(function (el) { return V.normalize(el.textContent); });
            if (follower) { try { follower.destroy(); } catch (_) {} }
            follower = V.create({
                slug: o.slug, lang: o.lang || window.__SPEECH_LANG, words: tokens,
                autoRestart: function () { return voiceOn; },
                onMove: function (idx) {
                    var el = words[idx]; if (!el) return;
                    if (cur) cur.classList.remove(curClass); cur = el; el.classList.add(curClass);
                    scroll.scrollTop = Math.max(0, el.offsetTop + el.offsetHeight / 2 - scroll.clientHeight * line);
                },
                onLock: function (on) { state(on ? 'tp_voice_locked' : 'tp_voice_searching'); },
                onListening: function (on) { if (on && voiceOn) state('tp_voice_listening'); },
                onDebug: o.onDebug,
                onFatal: function (reason) {
                    voiceOn = false; host(false);
                    state('tp_voice_fallback');
                    ev('tp_voice_fatal', { reason: String(reason || '') });
                    if (api._running) timed();     // the strip must keep moving
                }
            });
            voiceOn = true; host(true); scroll.style.scrollBehavior = 'smooth';
            if (!follower.start()) { voiceOn = false; host(false); state('tp_voice_fallback'); return false; }
            ev('tp_voice_start', { words: tokens.length });
            return true;
        }
        function timed() {
            scrolling = true; var last = null;
            (function step(ts) {
                if (!scrolling) return;
                if (last != null) {
                    var dt = (ts - last) / 1000;
                    scroll.scrollTop += (+(typeof o.speed === 'function' ? o.speed() : o.speed) || 80) * dt;
                }
                last = ts;
                if (scroll.scrollTop + scroll.clientHeight < scroll.scrollHeight + 40) raf = win.requestAnimationFrame(step);
                else scrolling = false;
            })(win.performance.now());
        }
        function halt() { scrolling = false; if (raf) { try { win.cancelAnimationFrame(raf); } catch (_) {} raf = 0; } }
        var api = {
            _running: false,
            setText: function (t) { txt = String(t || ''); render(); },
            setVoice: function (on) { want = !!on; render(); },
            voiceActive: function () { return voiceOn; },
            start: function () { api._running = true; render(); scroll.scrollTop = 0; if (startVoice()) return; timed(); },
            unpause: function () { api._running = true; if (voiceOn) { if (follower) follower.start(); return; } timed(); },
            pause: function () { api._running = false; halt(); if (voiceOn && follower) follower.stop(); },
            stop: function () { api._running = false; halt(); stopVoice(); }
        };
        return api;
    }

    window.ScriptPrompter = { create: create };
})();
